import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { RadioProps } from './Radio.types';
import { radioDotVariants, radioRootVariants } from './Radio.variants';

/**
 * Cross-platform Radio (single-select control). Renders identically on web and
 * React Native — a bordered circle with a centered dot shown when `checked`.
 * Mirrors the Figma "Flashka Design System" node `3419:1375`. See
 * `Radio.variants.ts` for the per-state spec + anatomy.
 *
 * Controlled-only. Radios select but never toggle off, so `onCheckedChange`
 * always reports `true` — the owning group applies the selection:
 *
 * @example
 * ```tsx
 * import { Radio } from '@flashka/shared/ui';
 *
 * const [value, setValue] = useState('a');
 * <Radio checked={value === 'a'} onCheckedChange={() => setValue('a')} accessibilityLabel="Option A" />
 * <Radio checked={value === 'b'} onCheckedChange={() => setValue('b')} accessibilityLabel="Option B" />
 * ```
 */
export function Radio({
  checked = false,
  onCheckedChange,
  size = 'm',
  disabled = false,
  interactive = true,
  haptic = 'selection',
  className,
  dotClassName,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  onHoverIn,
  onHoverOut,
}: RadioProps) {
  const boxClass = cn(radioRootVariants({ size, selected: checked }), className);
  const dot = checked && (
    <View
      webProps={{ 'aria-hidden': true }}
      className={cn(radioDotVariants({ size }), dotClassName)}
    />
  );

  // Presentational mode — a pure visual indicator (no role, focus, press, or
  // haptic) for embedding inside a larger clickable card/row that owns the
  // radio semantics. `pointer-events-none` so it never intercepts the parent's
  // press or reacts to hover; hidden from assistive tech.
  if (!interactive) {
    return (
      <View
        className={cn(boxClass, 'web:pointer-events-none')}
        testID={testID}
        webProps={{ 'aria-hidden': true, ...(webProps ?? {}) }}
        nativeProps={{ accessible: false, ...(nativeProps ?? {}) }}>
        {dot}
      </View>
    );
  }

  const handlePress = (event: PressEvent) => {
    // A radio press always means "select me" — never toggles off.
    if (!disabled) onCheckedChange?.(true, event);
  };

  // Announce radio + state to assistive tech on both platforms. Caller props
  // win on key overlap.
  const mergedWebProps = { 'aria-checked': checked, ...(webProps ?? {}) };
  const mergedNativeProps = {
    accessibilityState: { checked, disabled },
    ...(nativeProps ?? {}),
  };

  return (
    <Pressable
      className={boxClass}
      disabled={disabled}
      haptic={haptic}
      onPress={handlePress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityRole="radio"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}>
      {/* The Pressable carries the radio role + label; the dot is decorative. */}
      {dot}
    </Pressable>
  );
}
(Radio as { displayName?: string }).displayName = 'Radio';
